<script setup>
import {
  OmnichannelChatSDK,
  isCustomerMessage,
} from '@microsoft/omnichannel-chat-sdk';
import { onMounted, onUnmounted, ref, toRaw } from 'vue';
import {
  ChatBubbleOvalLeftIcon,
  Bars3BottomRightIcon,
  XMarkIcon,
  MinusIcon,
} from '@heroicons/vue/24/solid';

import LoadingThingy from './components/LoadingThingy.vue';
import InternalSurveyForm from './components/InternalSurveyForm.vue';
import ChatFooter from './components/ChatFooter.vue';
import ChatEnd from './components/ChatEnd.vue';
import { departmentMapping, buildOOHMessage, addAgentAvailabilityToChannel } from './rules';
import { MESSAGES, UNAVAILABLE_REASON } from './constants';
import messsageSound from './assets/message-received.mp3';

defineProps({
  isInternal: {
    type: Boolean,
    default: false
  }
})
const omnichannelConfig = {
  orgUrl: import.meta.env.VITE_ORG_URL,
  orgId: import.meta.env.VITE_ORG_ID,
  widgetId: import.meta.env.VITE_APP_ID,
};

const chatSDKConfig = {
  telemetry: {
    disable: true, // Disable telemetry
  },
};

const messageNotification = new Audio(messsageSound);
const audioPlayEnabled = ref(false);
const userMuted = ref(false);
const isLoading = ref(true);
const closedExtra = ref(false);
const showChatWidget = ref(false);
const preChatQuestions = ref([]);
const waiting_to_start = ref(null);
const initializeError = ref(null);

const channelsMapping = ref({});
const preChatSurveyResponses = ref({
});
const chatMessages = ref([]);
const isTyping = ref(false);
const messageToSend = ref('');
const sendingMessage = ref(false);
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const emailTranscript = ref('');
const confirmCloseChat = ref(false);
const confirmDecisionState = ref(false);
const showPostChatSurvey = ref(false);
const showQualtricsSurvey = ref(false);
const transcriptSentFailed = ref('');
const transcriptSending = ref(false);
const transcriptSent = ref(false);
const currentChannel = ref("");
const chatId = ref(null);
const labelToIdMap = ref({});
const customerName = ref(null);
const agentName = ref(null);
const surveyUrl = ref(null);
const offset = ref(0);
const url = import.meta.env.VITE_AGENT_AND_OOH_INFO_URL
const utc_now = ref("");

// const videoCallingSDK = ref(null);
// const chatToken = ref(null);
onMounted(async () => {
  console.log('called onMounted');
  isLoading.value = false;
  const channelFromSession = JSON.parse(sessionStorage.getItem('MS365CurrentChannel'));
  if (channelFromSession) {
    await initializeChat(channelFromSession?.id || null, Boolean(channelFromSession?.id));
  }
  //await initializeChat('581face5-17d2-458c-b83e-46a65d0d12c1');
});


let chatSDK = null

const initializeChat = async (widgetId, hasExistingSession = false) => {
  isLoading.value = true;
  if (!widgetId) {
    console.log('Widget Id is not provided');
    initializeError.value = MESSAGES.ERROR_INITIATE_CHAT
    isLoading.value = false;
    return
  }
  try {
    if (chatSDK && chatSDK?.chatToken?.chatId) {
      if (confirm(MESSAGES.END_CHAT_CONFIRMATION) === true) {
        await chatSDK.endChat();
        cleanUp();
        clearSurvey();
      } else {
        throw new Error('Chat already in progress');
      }
    }
  }
  catch (e) {
    isLoading.value = false;
    return;
  }
  if (!hasExistingSession) {
    cleanUp()
  }
  const _url = url + '?' + new URLSearchParams({
    "app_id": widgetId,
  });
  const channelDetails = await fetch(_url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((response) => response.json())
  //const channelDetails = await loadChannelDetails(widgetId);
  let { data, utcNow } = channelDetails;
  data = {
    "name": data.channelName,
    "id": data.channelAppId,
    ...data
  }
  currentChannel.value = data;
  // Check for OOH First before agent availability
  const channelWithAvailablility = addAgentAvailabilityToChannel(data, utcNow, 0);
  if (channelWithAvailablility.isUnAvailable && channelWithAvailablility.reason === UNAVAILABLE_REASON.OOH) {
    console.log('Chat Initiated in OOH');
    const oohMessage = buildOOHMessage(data);
    initializeError.value = `${MESSAGES.OOH_MESSAGE} ${oohMessage}`;
    isLoading.value = false;
    showChatWidget.value = true;
    return
  }
  // Agent Availability Check
  if (channelWithAvailablility.isUnAvailable && channelWithAvailablility.reason === UNAVAILABLE_REASON.NO_AGENTS) {
    console.log(MESSAGES.AGENTS_NOT_AVAILABLE);
    initializeError.value = MESSAGES.AGENTS_NOT_AVAILABLE
    isLoading.value = false;
    showChatWidget.value = true;
    return

  }

  initializeError.value = '';
  chatSDK = new OmnichannelChatSDK({
    ...omnichannelConfig,
    widgetId: widgetId
  }, chatSDKConfig);
  await chatSDK.initialize();
  preChatQuestions.value = await chatSDK.getPreChatSurvey();
  const checkPreviousSession = sessionStorage.getItem('MS365ChatSession');
  if (checkPreviousSession) {
    try {
      const optionalParams = {};
      optionalParams.liveChatContext = JSON.parse(checkPreviousSession);
      await chatSDK.startChat(optionalParams);
      chatMessages.value = await chatSDK.getMessages();
      await initializeChatEvents();
      showChatWidget.value = true;
      closedExtra.value = true;
      preChatSurveyResponses.value = JSON.parse(
        sessionStorage.getItem('MS365ChatPreChatSurvey')
      );
      setTimeout(() => {
        document
          .getElementById('chatInputBox')
          ?.scrollIntoView({ behavior: 'smooth' });
        document.getElementById('chatInputBox')?.focus();
      }, 500);
    } catch (e) {
      clearSessionStore()
    }
  }
  isLoading.value = false;
  showChatWidget.value = true;
}

const clearSessionStore = () => {
  sessionStorage.removeItem('MS365ChatSession');
  sessionStorage.removeItem('MS365ChatPreChatSurvey');
  sessionStorage.removeItem('MS365CurrentChannel');
}

onUnmounted(() => {
  console.log('called onUnmounted');
  //clearSessionStore()
});


const toggleMinimise = () => {
  //console.log('Called Toggle Minimise');
  closedExtra.value = !closedExtra.value;
};

// Called when the chat widget is toggled eg: minimise of maximise
const toggleChat = async () => {
  initializeAudio();
  //console.log('Called Toggle Chat');
  if (confirmCloseChat.value) return;

  showChatWidget.value = !showChatWidget.value;
  if (showChatWidget.value) {
    isLoading.value = true;
    closedExtra.value = true;
    await loadChannels();
    isLoading.value = false;
    if (chatMessages.value.length > 0) {
      setTimeout(() => {
        document
          .getElementById('chatInputBox')
          ?.scrollIntoView({ behavior: 'smooth' });
        document.getElementById('chatInputBox')?.focus();
      }, 500);
    }
  }
};

const initializeChatEvents = async () => {
  await chatSDK.onNewMessage((message) => {
    if (audioPlayEnabled.value) {
      messageNotification.play();
    }
    if (
      chatMessages.value.length === 1 &&
      chatMessages.value[0].id === '00000000-0000-0000-0000-000000000000'
    ) {
      chatMessages.value = [];
    }
    if (waiting_to_start.value !== null) {
      clearTimeout(waiting_to_start.value);
      waiting_to_start.value = null;
    }
    showChatWidget.value = true;
    if (
      chatMessages.value.length === 1 &&
      message.content === chatMessages.value[0].content
    ) {
      return;
    }
    chatMessages.value = [message, ...chatMessages.value];
    setTimeout(() => {
      document
        .getElementById('chatInputBox')
        ?.scrollIntoView({ behavior: 'smooth' });
      document.getElementById('chatInputBox')?.focus();
    }, 500);
    if (!isCustomerMessage(message)) {
      agentName.value = message.sender.displayName;
      chatId.value = chatSDK?.chatToken?.chatId;
    }
  });
  await chatSDK.onTypingEvent(() => {
    isTyping.value = true;
    setTimeout(() => {
      isTyping.value = false;
    }, 1000);
  });
  await chatSDK.onAgentEndSession(() => {
    //alert("Agent ended the session. Please refresh the page to start a new session.")
    clearSessionStore()
    endChat(true);
  });
  // chatToken.value = await chatSDK.getChatToken();
};

const checkAndSyncMessages = async () => {
  chatMessages.value = await chatSDK.getMessages();
  if (chatMessages.value.length === 0) {
    chatMessages.value = [
      {
        id: '00000000-0000-0000-0000-000000000000',
        content: 'Connecting...',
        sender: {
          displayName: '',
        },
        timestamp: new Date().toISOString(),
      },
    ];
    setTimeout(() => {
      document
        .getElementById('chatInputBox')
        ?.scrollIntoView({ behavior: 'smooth' });
      document.getElementById('chatInputBox')?.focus();
    }, 500);
    waiting_to_start.value = setTimeout(async () => {
      chatMessages.value = await chatSDK.getMessages();
      if (chatMessages.value.length === 0) {
        chatMessages.value = [
          {
            id: '00000000-0000-0000-0000-000000000000',
            content: MESSAGES.WAITING_FOR_AGENT,
            sender: {
              displayName: '',
            },
            timestamp: new Date().toISOString(),
          },
        ];
      }
    }, 5000);
  }
};

const chatAction = () => {
  toggleChat();
}


// Start Chat on click of start button inside chat or on click of chat icon if in sales page
const startChat = async (preChatResponse, mapping) => {
  // Reset transcriptSent to false
  transcriptSent.value = false;
  labelToIdMap.value = mapping
  customerName.value = preChatResponse[mapping["Name"]] ?? "";
  preChatResponse = {
    Type: 'InputSubmit',
    ...preChatResponse,
  };
  try {
    initializeAudio(true);
    isLoading.value = true;
    const customChatContext = {
      Source: { value: window?.location?.href || 'Unknown', isDisplayable: true },
    };
    await chatSDK.startChat({
      preChatResponse: preChatResponse,
      customContext: customChatContext,
      sendDefaultInitContext: true,
    });
    const chatContext = await chatSDK.getCurrentLiveChatContext();
    sessionStorage.setItem('MS365ChatSession', JSON.stringify(chatContext));
    sessionStorage.setItem(
      'MS365ChatPreChatSurvey',
      JSON.stringify(preChatSurveyResponses.value)
    );
    sessionStorage.setItem('MS365CurrentChannel', JSON.stringify(currentChannel.value));
    await initializeChatEvents();
    await checkAndSyncMessages();
    isLoading.value = false;
  } catch (e) {
    // alert('Something went wrong. Please try again.');
    isLoading.value = false;
    if (e.message === 'ConversationInitializationFailure') {
      initializeError.value = MESSAGES.ERROR_INITIATE_CHAT;
    } else if (e.message === 'WidgetUseOutsideOperatingHour') {
      initializeError.value = MESSAGES.AGENTS_NOT_AVAILABLE;
    }
  }
};

const initializeAudio = (unmute = false) => {
  if (!audioPlayEnabled.value) {
    messageNotification.play().then(() => {
      messageNotification.pause();
      messageNotification.currentTime = 0;
      audioPlayEnabled.value = true;
    });
  }
  if (unmute) {
    userMuted.value = false;
  }
  if (!audioPlayEnabled.value) {
    userMuted.value = true;
  }
};

const sendMessage = async () => {
  if (messageToSend.value.length === 0) {
    return;
  }
  //initializeAudio(false);
  sendingMessage.value = true;
  await chatSDK.sendMessage({
    content: messageToSend.value,
  });
  chatMessages.value = [
    {
      id: '00000000-0000-0000-0000-000000000000',
      content: messageToSend.value,
      sender: {
        displayName: 'Customer',
      },
      timestamp: new Date().toISOString(),
    },
    ...chatMessages.value,
  ];
  sendingMessage.value = false;
  messageToSend.value = '';
  setTimeout(() => {
    document
      .getElementById('chatInputBox')
      ?.scrollIntoView({ behavior: 'smooth' });
    document.getElementById('chatInputBox')?.focus();
  }, 100);
};

const customerTyping = async (event) => {
  if (event.key === 'Enter' && messageToSend.value.length > 0) {
    await sendMessage();
  }
};

// Called when close button is clicked when in form screen
const endChat = async (confirmed = false) => {
  //console.log('called endChatWindow', confirmed);
  if (!confirmed) {
    surveyUrl.value = buildSurveyUrl(preChatSurveyResponses, chatId, agentName, labelToIdMap);
  }
  if (confirmed) {
    preChatSurveyResponses.value = {};
    clearSessionStore()
  }
  if (chatMessages.value.length === 0) {
    showChatWidget.value = false;
    return;
  }
  confirmCloseChat.value = true;
  confirmDecisionState.value = true;
  if (confirmed) {
    emailTranscript.value = preChatSurveyResponses.value.email;
    showPostChatSurvey.value = true;
  }
};

const cancelEndChat = () => {
  //console.log('called cancelEndChat');
  confirmCloseChat.value = false;
  confirmDecisionState.value = false;
};

const cleanFieldsAndValidations = () => {
  //console.log('called cleanFieldsAndValidations');
  preChatSurveyResponses.value = {};
};


const cleanUp = () => {
  // console.log('called cleanUp');
  cleanFieldsAndValidations();
  clearSessionStore();
  showChatWidget.value = false;
  closedExtra.value = false;
  chatMessages.value = [];
  confirmCloseChat.value = false;
  confirmDecisionState.value = false;
  showPostChatSurvey.value = false;
  chatId.value = null;
  agentName.value = null;
  surveyUrl.value = null;
  labelToIdMap.value = {};
  customerName.value = null;
  currentChannel.value = {};
  clearSurvey();
};

const closeChatWindow = async (endChat = true) => {
  // console.log('called closeChatWindow'.endChat);
  cleanUp();
  // End Chat only when transcript is not sent, else end chat is called in requestEmailTranscript
  if (endChat && !transcriptSent.value) {
    transcriptSent.value = false;
    await chatSDK.endChat();
  }
};

const startNewChat = async (endChat = true) => {
  // console.log('called startNewChat'. endChat);
  let _currentChannelId = currentChannel?.value?.id;
  cleanUp();
  // Call end chat only when transcript is not sent, else end chat is called in requestEmailTranscript
  if (!transcriptSent.value) {
    transcriptSent.value = false;
    await chatSDK.endChat();
  }
  await initializeChat(_currentChannelId);
};

const downloadFile = (fileMetadata) => {
  chatSDK
    .downloadFileAttachment(toRaw(fileMetadata))
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Set link's href to point to the Blob URL
      link.href = blobUrl;
      link.download = fileMetadata.name;

      // Append link to the body
      document.body.appendChild(link);

      // Dispatch click event on the link
      // This is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      );
      // Remove link from body
      document.body.removeChild(link);
    })
    .catch((e) => {
      console.log(e);
      // alert('Unable to download attachment.');
    });
};

const processUpload = async (evt) => {
  const file = evt.target.files[0];
  try {
    sendingMessage.value = true;
    const resp = await chatSDK?.uploadFileAttachment(file);
    //console.log(resp);
    chatMessages.value = [
      {
        id: resp.id,
        ...resp,
      },
      ...chatMessages.value,
    ];
    sendingMessage.value = false;
    evt.target.value = null;
    //console.log(chatMessages.value);
  } catch (e) {
    console.log(e);
    // alert('Unable to upload file.');
  }
};

const requestEmailTranscript = async () => {
  if (!emailPattern.test(emailTranscript.value)) {
    transcriptSentFailed.value = 'Please enter a valid email.';
    return;
  }
  try {
    transcriptSending.value = true;
    transcriptSentFailed.value = '';
    await chatSDK.emailLiveChatTranscript({
      emailAddress: emailTranscript.value,
      attachmentMessage: 'Your Chat Transcript',
    });
    transcriptSending.value = false;
    transcriptSent.value = true;
    await chatSDK.endChat();
  } catch (e) {
    transcriptSending.value = false;
    console.log(e);
    transcriptSentFailed.value = 'Unable to email transcript.';
  }
};

//Set this once chatsdk is initialized
window.initiateChat = async (secretKey, widgetId) => {
  //console.log(secretKey, widgetId)
  if (secretKey === 'CAB724D7E74F5') {
    await initializeChat(widgetId);
  }
};

window.destroyChat = async (secretKey) => {
  if (secretKey === 'CAB724D7E74F5') {
    if (chatSDK && chatSDK?.chatToken?.chatId) {
      await chatSDK.endChat();
    }
    cleanUp();
  }
};

const buildSurveyUrl = (preChatSurveyResponses, chatId, agentName, labelToIdMap) => {
  labelToIdMap = labelToIdMap.value || {};
  const channelId = preChatSurveyResponses.value.channel_id;
  const agentNameFormatted = agentName.value ? `${channelId}-${agentName.value}` : "";
  const chatIdFinal = chatId.value ? chatId.value : "";
  const customerName = preChatSurveyResponses.value[labelToIdMap["Name"] || ""] || "";
  const customerEmail = preChatSurveyResponses.value[labelToIdMap["Email"] || ""] || "";
  const customerPhone = preChatSurveyResponses.value[labelToIdMap["Phone Number"] || ""] || "";
  const location = departmentMapping.find((dept) => dept.dept === channelId)?.deptId || channelId || "";
  return `https://ziplyfiber.sjc1.qualtrics.com/jfe/form/SV_eqXCFSpkf31O68d?location=${location}&TriggeredAt=${new Date().toISOString()}&CustomerName=${customerName}&CustomerEmail=${customerEmail}&CustomerPhone=${customerPhone}&agent_id=${agentNameFormatted}&ChatId=${chatIdFinal}`;

}

const clearSurvey = () => {
  var qualtricsSurvey = document.getElementById('qualtricsSurvey');
  if (qualtricsSurvey) {
    qualtricsSurvey.style.display = 'none';
    if (qualtricsSurvey.childNodes.length > 0) {
      qualtricsSurvey.removeChild(qualtricsSurvey.childNodes[0]);
    }
  }
  showQualtricsSurvey.value = false;
};

</script>

<template>
  <div id="chatWrapper" class="chatWrapper" v-if="currentChannel?.id">
    <!-- Chat Icon Start-->
    <div id="chatActivator"
      class="!zf-z-[1100] zf-fixed !zf-right-6 !zf-bottom-6 sm:zf-right-12 sm:zf-bottom-12 zf-flex zf-gap-4 zf-items-center">
      <div
        class="!zf-bg-gray-50 zf-shadow !zf-flex zf-items-center zf-justify-center !zf-px-4 !zf-h-fit !zf-py-2 zf-relative md:zf-flex zf-hidden chat-help"
        v-if="!isLoading && !showChatWidget && !closedExtra">
        <button class="!zf-fill-white !zf-bg-ziply-blue-dark zf-rounded-full zf-absolute -zf-left-2 -zf-top-2 !zf-p-1"
          @click="closedExtra = true">
          <XMarkIcon class="!zf-w-3 !zf-h-3 zf-fill-white" />
        </button>
        <p>Chat With Us</p>
      </div>
      <div
        class="zf-w-12 zf-h-12 zf-rounded-full zf-cursor-pointer zf-shadow-lg hover:zf-bg-ziply-blue-dark !zf-bg-ziply-blue md:zf-w-24 md:zf-h-24 md:zf-rounded-[4px]"
        v-if="!showChatWidget" :disaled="isLoading" @click="chatAction()">
        <LoadingThingy v-if="isLoading" class="!zf-fill-gray-100" />
        <div class="!zf-flex zf-w-full !zf-h-full !zf-justify-center !zf-items-center" v-if="!isLoading">
          <div class="!zf-text-center">
            <ChatBubbleOvalLeftIcon class="zf-w-6 zf-h-6  md:zf-w-12 md:zf-h-12 !zf-fill-gray-100"
              v-if="!showChatWidget" />
            <p class="!zf-text-gray-200 zf-text-3.5 md:zf-text-xl md:zf-flex zf-hidden zf-m-0 zf-text-white">
              Chat
            </p>
          </div>
        </div>
      </div>
      <div
        class="zf-w-12 zf-h-12 zf-rounded-full zf-cursor-pointer zf-shadow-lg hover:zf-bg-ziply-blue-dark zf-circle-button md:zf-w-16 md:zf-h-16 !zf-bg-[#000050]"
        v-if="showChatWidget" :disaled="isLoading" @click="chatAction()">
        <LoadingThingy v-if="isLoading" class="!zf-fill-gray-100" />
        <div class="!zf-flex zf-w-full !zf-h-full !zf-justify-center !zf-items-center" v-if="!isLoading">
          <div class="!zf-text-center">
            <Bars3BottomRightIcon class="zf-w-6 zf-h-6  md:zf-w-12 md:zf-h-12 !zf-fill-gray-100" v-if="showChatWidget" />
          </div>
        </div>
      </div>
    </div>
    <!-- Chat Icon End -->
    <div id="chatContainer"
      class="!zf-z-[1100] zf-fixed zf-bg-white zf-rounded zf-shadow zf-overflow-hidden !zf-w-12/12 !zf-h-[544px] sm:zf-w-[400px] !sm:zf-h-[546px] !zf-right-6 !zf-bottom-[100px] !sm:zf-right-16 !sm:zf-bottom-[126px] zf-cursor-pointer !zf-border !zf-border-white"
      v-if="showChatWidget">
      <div
        class="!zf-h-[9%] !zf-bg-ziply-blue !zf-text-gray-100 !zf-flex !zf-justify-between !zf-w-full !zf-items-center !zf-px-4 !zf-rounded-t">
        <MinusIcon class="!zf-h-6 !zf-w-6 !mr-3 !zf-fill-gray-100 zf-cursor-pointer" v-if="!confirmDecisionState"
          @click="toggleChat()" />
        <p class="!zf-text-center !zf-w-full !zf-text-lg zf-text-white zf-m-0">
          {{ currentChannel?.name ? `Chat with ${currentChannel?.name}` : "Chat" }}
        </p>
        <XMarkIcon id="endChatButton" class="!zf-h-6 !zf-w-6 !zf-fill-gray-100 !zf-cursor-pointer zf-end-chat-button"
          v-if="!confirmCloseChat" @click="endChat()" />
        <XMarkIcon id="closeChatButton" class="!zf-h-6 !zf-w-6 !zf-fill-gray-100 !zf-cursor-pointer zf-close-chat-button"
          v-if="showQualtricsSurvey || confirmCloseChat" v-show="showPostChatSurvey" @click="closeChatWindow(true)" />
      </div>
      <!-- Chat Body-->
      <div class="!zf-h-[91.2%] !zf-w-full !zf-text-[#3c3c3c] zf-overflow-hidden">
        <!-- Welcome message and form -->
        <div v-if="chatMessages.length === 0" class="!zf-p-6 !zf-h-full !zf-overflow-scroll zf-no-scrollbar">
          <p class="!zf-mb-8 !zf-text-justify" v-if="!isLoading && !initializeError">
            To proceed with this chat, please complete the following and click <strong>Start Chat</strong>.
          </p>
          <p class="!zf-text-red-700 !zf-mb-3 !zf-text-sm zf-p-0" v-if="initializeError">
            <span class="zf-whitespace-pre-wrap">
              {{ initializeError }}
            </span>
          </p>
          <p v-if="isLoading">Processing...</p>
          <InternalSurveyForm :channelOptions="channelOptions" :start-chat="startChat" v-if="!initializeError"
            :pre-chat-survey-responses="preChatSurveyResponses" :pre-chat-questions="preChatQuestions"
            :is-loading="isLoading" :is-internal="isInternal" />
        </div>
        <template v-else>
          <div class="!zf-flex  zf-flex-col !zf-gap-2 !zf-h-full" v-if="!confirmCloseChat">
            <!-- Chat messages -->
            <div id="scrollContainer"
              class="!zf-flex !zf-flex-col-reverse !zf-h-[90%] !zf-px-4 !zf-py-2 !zf-overflow-scroll zf-no-scrollbar">
              <div v-for="message in chatMessages" :key="message.id" class="!zf-flex !zf-flex-col !zf-gap-1 ">
                <div class="!zf-flex !zf-flex-col !zf-items-start" v-if="!isCustomerMessage(message)">
                  <div class="!zf-text-ziply-blue !zf-flex !zf-justify-center !zf-items-center">
                    <p class="!zf-text-sm !zf-text-ziply-blue !zf-font-normal">
                      {{ agentName || message.sender.displayName }}
                    </p>
                  </div>
                  <div class="!zf-bg-gray-100 zf-rounded-lg !zf-px-4 !zf-py-2" v-if="message.content !== '' &&
                    typeof message.content !== 'object'
                    ">
                    <p class="!zf-text-gray-800 !zf-text-sm !zf-text-left">
                      {{ message.content }}
                    </p>
                  </div>
                  <div class="!zf-bg-gray-100 !zf-rounded-lg !zf-px-4 !zf-py-2" v-if="message.fileMetadata !== undefined">
                    <button class="!zf-text-blue-600 !zf-text-sm zf-underline"
                      @click="downloadFile(Object.freeze(message.fileMetadata))">
                      {{ message.fileMetadata.name }}
                    </button>
                  </div>
                  <div v-if="message.timestamp !== ''">
                    <p class="zf-italic zf-text-xs zf-text-gray-500">
                      {{ new Date(message.timestamp).toLocaleString() }}
                    </p>
                  </div>
                </div>
                <div class="!zf-flex !zf-flex-col !zf-items-end" v-else>
                  <div class="!zf-text-slate-800 !zf-rounded-full !zf-flex !zf-justify-center !zf-items-center">
                    <p class="!zf-text-sm !zf-font-normal !zf-text-gray-600">
                      {{ customerName || preChatSurveyResponses.name }}
                    </p>
                  </div>
                  <div class="!zf-bg-gray-200 !zf-rounded-lg !zf-p-2" v-if="message.content !== '' &&
                    typeof message.content !== 'object'
                    ">
                    <p class="!zf-text-gray-800 !zf-text-sm !zf-text-right zf-break-words">
                      {{ message.content }}
                    </p>
                  </div>
                  <div class="!zf-bg-gray-100 !zf-rounded-lg !zf-px-4 !zf-py-2" v-if="message.fileMetadata !== undefined">
                    <button class="!zf-text-blue-600 !zf-text-sm !zf-underline"
                      @click="downloadFile(Object.freeze(message.fileMetadata))">
                      {{ message.fileMetadata.name }}
                    </button>
                  </div>
                  <div v-if="message.timestamp !== ''">
                    <p class="zf-italic zf-text-xs zf-text-gray-500">
                      {{ new Date(message.timestamp).toLocaleString() }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Chat Messages End-->
            <!-- Chat Footer-->
            <ChatFooter :is-typing="isTyping" :message-to-send="messageToSend"
              @update:message-to-send="newValue => messageToSend = newValue" :process-upload="processUpload"
              :sending-message="sendingMessage" :send-message="sendMessage" :customer-typing="customerTyping" />
          </div>
          <div class="!zf-w-full !zf-h-full" v-if="confirmCloseChat" :class="{
            '!zf-bg-white': showPostChatSurvey,
            'zf-bg-opaque': !showPostChatSurvey,
          }">
            <!-- Close Confirmation-->
            <div v-if="!showPostChatSurvey" class="zf-text-center zf-pl-8 zf-pt-36 zf-pb-8">
              <p class="zf-text-white">Are you sure you want to end the chat?</p>
              <div class="zf-flex zf-flex-col zf-gap-5 mt-4 zf-justify-center zf-px-16">
                <button
                  class="!zf-px-8 !zf-py-2 zf-rounded-full zf-bg-transparent zf-border-2 zf-border-gray-300 hover:zf-bg-gray-300 zf-text-gray-100 hover:zf-text-black zf-cursor-pointer"
                  @click="endChat(true)">
                  End Chat
                </button>
                <button
                  class="!zf-px-8 !zf-py-2 zf-rounded-full zf-bg-transparent zf-border-2 zf-border-gray-300 hover:zf-bg-gray-300 zf-text-gray-100 hover:zf-text-black zf-cursor-pointer"
                  @click="cancelEndChat()">
                  Cancel
                </button>
              </div>
            </div>
            <template v-else>
              <ChatEnd :email-transcript=emailTranscript @update:email-transcript="newValue => emailTranscript = newValue"
                :transcript-sent-failed="transcriptSentFailed"
                @update:transcript-sent-failed="newValue => transcriptSentFailed = newValue"
                :show-qualtrics-survey="showQualtricsSurvey"
                @update:show-qualtrics-survey="newValue => showQualtricsSurvey = newValue"
                :request-email-transcript="requestEmailTranscript" :transcript-sent="transcriptSent"
                :transcript-sending="transcriptSending" :start-new-chat="startNewChat" :survey-url="surveyUrl"
                :is-internal="true" />
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
